import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../lib/cn'

/**
 * Variant styles for {@link Badge}.
 *
 * All sizing, spacing, and color classes resolve to design tokens from @atlas/tokens
 * (brand). No hardcoded values.
 *
 * @remarks
 * - Supports six variants: primary, secondary, danger, success, warning, muted
 * - Supports three sizes: sm, md (default), lg
 * - Inline-flex for text and optional icon
 *
 * @example
 * ```tsx
 * const classes = badgeVariants({ variant: 'primary', size: 'md' })
 * // Returns: "inline-flex items-center rounded-ds-full ... bg-ds-primary ..."
 * ```
 */
export const badgeVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap font-normal rounded-ds-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring',
	{
		variants: {
			variant: {
				primary: 'bg-ds-primary text-ds-primary-foreground',
				secondary: 'bg-ds-secondary text-ds-text',
				danger: 'bg-ds-danger text-ds-danger-foreground',
				success: 'bg-ds-success text-ds-success-foreground',
				warning: 'bg-ds-warning text-ds-warning-foreground',
				muted: 'bg-ds-surface text-ds-text-muted border border-ds-border',
			},
			size: {
				sm: 'text-ds-sm px-2 py-0.5 h-5',
				md: 'text-ds-base px-3 py-1 h-6',
				lg: 'text-ds-lg px-4 py-1.5 h-8',
			},
		},
		defaultVariants: { variant: 'primary', size: 'md' },
	},
)

/**
 * Props for the {@link Badge} component.
 *
 * @property variant - Visual style: 'primary' | 'secondary' | 'danger' | 'success' | 'warning' | 'muted'
 * @property size - Badge size: 'sm' | 'md' (default) | 'lg'
 * @property className - Optional additional Tailwind classes
 * @property children - Badge text content
 *
 * @example
 * ```tsx
 * <Badge variant="primary" size="md">
 *   New
 * </Badge>
 * ```
 */
export interface BadgeProps extends React.HTMLAttributes<HTMLElement>, VariantProps<typeof badgeVariants> {
	/**
	 * Rendered element. `button` exists for the filter toggles that look like a
	 * badge and behave like a control — a span there would not be focusable or
	 * operable by keyboard. The caller owns `type`, `aria-pressed` and the handler.
	 */
	as?: 'span' | 'button'
	/** Button type, for `as="button"`. Defaulted by the caller, never implicitly 'submit'. */
	type?: 'button' | 'submit' | 'reset'
}

/**
 * Badge component for displaying status, labels, or counts.
 *
 * @remarks
 * - **Styling:** All colors and sizing use design tokens from `@atlas/tokens`
 *   (brand). No hardcoded values.
 * - **Variants:** Primary (brand primary), secondary, danger, success, warning, muted.
 * - **Sizes:** Small (h-5), medium (h-6, default), large (h-8).
 * - **Shape:** Rounded-full (pill shape).
 * - **Usage:** Status indicators, labels, notification counts, category tags.
 *
 * @example Basic usage
 * ```tsx
 * import { Badge } from '@atlas/ui-primitives'
 *
 * function MyComponent() {
 *   return (
 *     <div>
 *       <Badge variant="primary">New</Badge>
 *       <Badge variant="success">Active</Badge>
 *       <Badge variant="danger">Error</Badge>
 *     </div>
 *   )
 * }
 * ```
 *
 * @example With different sizes
 * ```tsx
 * <Badge size="sm" variant="muted">Small</Badge>
 * <Badge size="md">Medium</Badge>
 * <Badge size="lg" variant="success">Large</Badge>
 * ```
 *
 * @see {@link badgeVariants} for CVA variant configuration
 * @see packages/ui-primitives/src/badge/badge.stories.tsx for visual examples
 */
export function Badge({ as: Element = 'span', variant, size, className, ...props }: BadgeProps) {
	// `as` picks the tag at runtime, so TypeScript cannot narrow the intrinsic
	// element's props from the union; the cast lands on the tag the caller chose.
	const Tag = Element as 'span'
	return <Tag {...props} className={cn(badgeVariants({ variant, size }), className)} />
}
